import * as Sentry from '@sentry/react'
import { getEnv } from '@/lib/env'

/**
 * ────────────────────────────────────────────────────────────────────────────
 * La guarda de «esto no es producción» (goal `observabilidad-senal 4`)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Un error nacido en la máquina de quien desarrolla no puede llegar al panel
 * etiquetado como producción. En el tiraje del 2026-08-27 pasaba: en 90 días
 * `web-admin` recibió **70 eventos desde `http://localhost:3000` con
 * `environment: production`**. Además de contaminar cualquier métrica, es una
 * fuga: rutas de trabajo, nombres de usuario del sistema y lo que haya en la
 * sesión local salen del portátil y entran en la observabilidad compartida.
 *
 * Cuando la guarda salta, el SDK **se calla**. No se reetiqueta el evento ni se
 * redirige a otro proyecto (decisión del goal): reetiquetar sería inventar un
 * dato y redirigir exigiría un DSN que esa máquina no tiene por qué tener. La
 * aplicación arranca exactamente igual, solo se queda sin telemetría (§6.11).
 *
 * Es **defensa, no política**: la forma correcta de trabajar sigue siendo que
 * cada `.env` local declare su entorno; la guarda existe para el día que
 * alguien copie el `.env` de un compañero, que es lo que pasa en la práctica.
 */

/**
 * Hosts que solo existen en la máquina de quien desarrolla.
 *
 * 🚨 **No entran los rangos de LAN** (`192.168.*`, `10.*`, `172.16-31.*`) y es
 * a propósito: hay despliegues on-premise servidos por IP privada que son
 * producción de verdad, y meterlos aquí convertiría la guarda en un apagón de
 * telemetría. Solo el bucle local y los nombres que nunca resuelven fuera de la
 * máquina.
 */
export function esHostLocal(host: string | undefined): boolean {
  const limpio = (host ?? '').trim().toLowerCase().replace(/^\[|\]$/g, '')
  if (!limpio) return false
  if (limpio === 'localhost' || limpio.endsWith('.localhost')) return true
  if (limpio.endsWith('.local')) return true
  if (limpio === '::1' || limpio === '::') return true
  if (limpio === '0.0.0.0') return true
  // 127.0.0.0/8 entera: el servidor de desarrollo se ata a cualquiera de ellas.
  return /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(limpio)
}

/**
 * ¿El entorno declarado dice «producción»?
 *
 * 🚨 **Vacío CUENTA como producción**, y no es una precaución de más: cuando
 * `Sentry.init` recibe `environment` sin valor, el SDK etiqueta el evento como
 * `production` por su cuenta. Así llegaron exactamente los 70 eventos de
 * `localhost` del tiraje —el init de entonces ni siquiera pasaba
 * `environment`—, así que una guarda que solo mirase la palabra los habría
 * dejado pasar todos.
 *
 * `prod` está en la lista porque tres webs del ecosistema abrevian así (goal
 * `observabilidad-senal 2`). Un `staging` **no** es producción y no se calla.
 */
export function esEntornoDeProduccion(entorno: string | undefined): boolean {
  const limpio = (entorno ?? '').trim().toLowerCase()
  return (
    limpio === '' ||
    limpio === 'prod' ||
    limpio === 'production' ||
    limpio === 'produccion' ||
    limpio === 'producción'
  )
}

/** Host local **y** entorno de producción: la combinación que no puede existir. */
export function debeCallarseEnLocal(
  host: string | undefined,
  entorno: string | undefined
): boolean {
  return esHostLocal(host) && esEntornoDeProduccion(entorno)
}

/** Hostname del navegador, o cadena vacía si no hay ventana (tests, SSR). */
export function hostDelNavegador(): string {
  return typeof window === 'undefined' ? '' : (window.location?.hostname ?? '')
}

/**
 * El aviso. La guarda se calla para Sentry, no para quien está delante: si no
 * dijera por qué, el efecto sería idéntico al de un DSN mal cableado y se
 * descubriría semanas después buscando un error que nunca llegó al panel.
 */
export function avisarDeLaGuarda(host: string | undefined, entorno: string | undefined): void {
  // Aviso deliberado: la guarda se calla para Sentry, no para quien esta delante.
  console.warn(
    `[Sentry] No se inicia: esta página corre en un host local (${host}) y el entorno ` +
      `declarado es de producción (${entorno || 'sin declarar ⇒ production por defecto'}). ` +
      'Tus errores ensuciarían el panel de producción y sacarían de tu máquina rutas de ' +
      'trabajo y datos de tu sesión. Declara el entorno real en tu .env.'
  )
}

export function initSentry() {
  const dsn = getEnv('VITE_SENTRY_DSN')
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[Sentry] DSN no configurado, salto inicialización')
    }
    return
  }

  // 🚨 Guarda de «esto no es producción» (goal `observabilidad-senal 4`): un host
  // local etiquetado como producción no reporta. Ver el bloque de arriba.
  const host = hostDelNavegador()
  const entorno = getEnv('VITE_SENTRY_ENV') || import.meta.env.MODE
  if (debeCallarseEnLocal(host, entorno)) {
    avisarDeLaGuarda(host, entorno)
    return
  }

  Sentry.init({
    dsn,
    environment: entorno,
    release: getEnv('VITE_APP_VERSION'),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 0.05,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      if (import.meta.env.DEV) {
        const err = hint.originalException
        if (err instanceof Error && err.message.includes('ResizeObserver')) {
          return null
        }
      }
      return event
    },
  })
}

export { Sentry }
